import React, { memo, useCallback, useEffect } from "react";
import { css, SerializedStyles, useTheme } from "@emotion/react";
import FocusLock from "react-focus-lock";
import OutsideClickHandler from "react-outside-click-handler";

import { DOMUtils } from "core/includes/domUtils";
import Portal from "core/Components/Portal";
import { ReactComponent as CloseIcon } from "images/icons/cross-icon-2.svg";
import LinkTo from "core/Components/LinkTo";

type SizeType = "small"|"medium"|"max";

type Props = {
    children: React.ReactNode,
    className?: string,
    innerRef?: () => void,
    closeTo?: string,
    onClose?: (e: React.MouseEvent) => void,
    onOverlayClick?: () => void,
    closeOnOutsideClick?: boolean,
    overlay?: boolean,
    size?: SizeType,
    showCloseButton?: boolean,
    extraStyle?: SerializedStyles,
    closeButtonVariant?: 'square'|'circle-overflowing'|'circle',
    autoFocus?: boolean,
};

const Dialog: React.FC<Props> = ({
    children,
    className,
    innerRef,
    closeTo,
    onClose,
    onOverlayClick,
    closeOnOutsideClick = false,
    overlay = true,
    showCloseButton = true,
    size,
    extraStyle,
    closeButtonVariant = 'square',
    autoFocus = true,
}) => {
    const theme = useTheme();

    /**
     * Disable body scrolling on mobile.
     */
    useEffect(() => {
        if (DOMUtils.isMobile()) {
            DOMUtils.preventBodyScrolling();
        }

        return () => {
            if (DOMUtils.isMobile()) {
                DOMUtils.enableBodyScrolling();
            }
        };
    }, []);

    /**
     * Handle close button click.
     */
    const handleClose = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof onClose === "function") {
            onClose(e);
        } else if (typeof onOverlayClick === "function") {
            onOverlayClick();
        }
    }, [onClose, onOverlayClick]);

    const closeText = size === "max" ? "CLOSE" : "x";

    const overlayStyles = css`
        background-color: rgba(0, 0, 0, 0.3);
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 100;
    `;

    const containerStyles = (size?: SizeType, extraStyle?: SerializedStyles) => css`
        ${theme.popover.container};
        max-width: 80%;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 101;
        border-radius: 25px;

        ${theme.breakpoints.up("5xl")} {
            width: 752px;
            padding: 61px 46px 55px;
        }

        ${theme.breakpoints.between("md", "4xl")} {
            width: 608px;
        }

        ${theme.breakpoints.between("3xl", "4xl")} {
            padding: 40px 38px 36px;
        }

        ${theme.breakpoints.only("xxl")} {
            padding: 35px 38px 36px;
        }

        ${theme.breakpoints.between("md", "xl")} {
            padding: 30px 16px 29px;
        }

        ${theme.breakpoints.down("sm")} {
            padding: 15px 18px 22px;
        }

        ${theme.breakpoints.only("sm")} {
            width: 398px;
        }

        ${theme.breakpoints.down("xs")} {
            max-width: 332px;
        }

        ${extraStyle};
    `;

    const closeStyles = css`
        position: absolute;
        right: 0;
        top: 0;
        border-width: 0 0 1px 1px;
        border-color: ${theme.colours.grey[700]};
        border-style: solid;
        font-size: 16px;
        cursor: pointer;
        color: ${theme.colours.white};
        line-height: 20px;
        vertical-align: middle;
        text-align: center;
        display: block;
        width: 24px;
        height: 24px;
        background: ${theme.colours.grey[700]};

        &:hover {
            border-color: ${theme.colours.grey[500]};
            background: ${theme.colours.grey[500]};
        }

        ${!!closeTo && css`
            span {
                pointer-events: none;
            }
        `};
    `;

    const circleOverflowingStyle = css`
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(40%, -40%);
        width: 39px;
        height: 39px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        background: ${theme.colours.gray4};
        cursor: pointer;
        z-index: ${theme.zIndex.zIndexMedium};
        ${theme.mixins.transformedBloop('translate(40%, -40%)')};

        > svg {
            fill: white;
            width: 100%;

            ${!!closeTo && css` pointer-events: none; `};
        }

        :hover circle {
            fill: ${theme.colours.scorpion2};
        }
    `;

    const circleStyle = css`
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(-40%, 40%);
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        background: ${theme.colours.gray4};
        cursor: pointer;
        z-index: ${theme.zIndex.zIndexMedium};
        ${theme.mixins.transformedBloop('translate(-40%, 40%)')};

        > svg {
            fill: white;
            width: 100%;

            ${!!closeTo && css` pointer-events: none; `};
        }

        :hover circle {
            fill: ${theme.colours.scorpion2};
        }
    `;

    return (
        <Portal id="dialog-portal">
            <FocusLock autoFocus={autoFocus}>
                {overlay && (
                    <div
                        css={overlayStyles}
                        onClick={onOverlayClick ? onOverlayClick : () => {
                        }}
                    />
                )}
                <OutsideClickHandler onOutsideClick={handleClose} disabled={!closeOnOutsideClick}>
                    <div css={containerStyles(size, extraStyle)} ref={innerRef} className={className}>
                        {showCloseButton && (
                            <>
                                {closeButtonVariant === 'square' && (
                                    <LinkTo
                                        css={closeStyles}
                                        to={closeTo}
                                        isDisabled={!closeTo}
                                        onClick={handleClose}
                                    >
                                        <span onClick={handleClose}>
                                            {closeText}
                                        </span>
                                    </LinkTo>
                                )}
                                {closeButtonVariant === 'circle-overflowing' && (
                                    <LinkTo
                                        css={circleOverflowingStyle}
                                        to={closeTo}
                                        isDisabled={!closeTo}
                                        onClick={handleClose}
                                    >
                                        <CloseIcon onClick={handleClose} />
                                    </LinkTo>
                                )}
                                {closeButtonVariant === 'circle' && (
                                    <LinkTo
                                        css={circleStyle}
                                        to={closeTo}
                                        isDisabled={!closeTo}
                                        onClick={handleClose}
                                    >
                                        <CloseIcon onClick={handleClose} />
                                    </LinkTo>
                                )}
                            </>
                        )}
                        {children}
                    </div>
                </OutsideClickHandler>
            </FocusLock>
        </Portal>
    );
};

export default memo(Dialog);